// src/redux/thunks/authThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { broadcastLogout } from 'src/api/axiosInstance';
import { decryptData, encryptData } from 'src/utils/Crypto';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
import { clearPermissions, fetchPermissions } from '../slices/permissionSlice';
import { fetchPlanFeatures } from './planThunks';
import { clearPlanFeatures } from '../slices/planSlice';
import { clearSetupChecklist } from '../slices/setupChecklistSlice';
import { refreshTenantPreference } from './tenantPreferenceThunks';
// checkAuth
export const checkAuth = createAsyncThunk(
  'auth/check',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.get('/auth/me', {
        headers: {
          'X-Skip-Auth-Redirect': true,
        },
      });
      dispatch(fetchPermissions());
      dispatch(fetchPlanFeatures());
      dispatch(refreshTenantPreference());
      return true;
    } catch (err) {
      return rejectWithValue(false);
    }
  }
);

function formatSecondsToMinutes(seconds) {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;

  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')} minutes left`;
}

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ userEmail, userPassword, fingerPrint, deviceId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/auth/login', {
        email: userEmail,
        password: userPassword,
        fingerPrint,
        deviceId,
      });

      // OTP required
      if (res.status === 202 && res.data) {
        return res.data;
      }

      // Success
      if (res.status === 200) {
        const payloadUser = res.data?.user;
        if (payloadUser) {
          localStorage.setItem('user', JSON.stringify(payloadUser));
        }
        await dispatch(initializeUserSession()).unwrap();
        dispatch(fetchPermissions());
        dispatch(fetchPlanFeatures());
        dispatch(refreshTenantPreference());
        return res.data;
      }

      return res.data;
    } catch (err) {

      // <-- ADD THIS BLOCK
      if (typeof err === 'string') {
        return rejectWithValue(err);
      }

      if (!err.response) {
        console.error('Network error:', err);
        return rejectWithValue('Network error. Please check your connection.');
      }

      const { status, data } = err.response;
      const { message, remainingAttempts, retryAfterSeconds } = data || {};

      //  CASE 1: RATE LIMIT (429)
      if (status === 429) {
        return rejectWithValue(
          message
            ? 'Your account has been temporarily blocked due to security reasons. Please try again after 15 minutes.'
            : 'Account temporarily blocked due to security restrictions.'
        );
      }

      // CASE 2: INVALID CREDENTIALS + CUSTOM MAPPING
      if (status === 401) {
        const backendMessage = err?.response?.data?.message;
        const remainingAttempts1 = err?.response?.data?.remainingAttempts;

        //  Hide sensitive messages
        if (backendMessage === 'User end date is passed') {
          return rejectWithValue('Your account is currently inactive. Please contact your administrator.');
        }

        //  Handle frozen user — must be checked BEFORE remainingAttempts
        if (backendMessage && backendMessage.toLowerCase().includes('frozen')) {
          return rejectWithValue(
            'Your account has been temporarily suspended due to plan limits. Please contact your administrator to upgrade the plan.'
          );
        }

        //  Handle invalid credentials attempts
        if (typeof remainingAttempts1 === 'number') {
          return rejectWithValue(
            remainingAttempts1 <= 0
              ? 'Invalid credentials. You have no attempts left.'
              : `Invalid credentials. You still have ${remainingAttempts1} attempts left.`
          );
        }

        //  fallback to backend message
        return rejectWithValue(backendMessage || 'Invalid credentials.');
      }

      // CASE 3: PASSWORD EXPIRED (403)
      if (status === 403 || data?.errorCode === 'PASSWORD_EXPIRED') {
        let decryptedEmail = '';
        if (data?.email) {
          try {
            decryptedEmail = decryptData(data.email);
          } catch (e) {
            decryptedEmail = data.email;
          }
        }
        return rejectWithValue({
          errorCode: data?.errorCode || 'PASSWORD_EXPIRED',
          message: data?.message || 'Your password has expired. Please update it.',
          email: decryptedEmail,
        });
      }

      //  CASE 4: SERVER ERROR
      if (status >= 500) {
        return rejectWithValue('Server error. Please try again later.');
      }

      return rejectWithValue(message || 'Login failed.');
    }
  }
);

export const initializeUserSession = createAsyncThunk(
  'auth/initializeUserSession',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(clearSetupChecklist());
      const user = JSON.parse(localStorage.getItem('user'));
      const siteRes = await axiosInstance.post(`/rest/userrole/getbyuser`, {
        userId: encryptData(String(user.userId)),
      });

      const sites = siteRes.data || [];

      if (sites.length === 0) {
        localStorage.removeItem('user');

        return rejectWithValue(
          `You don't have a role assigned yet .        
           Please contact your administrator.`
        );
      }

      const activeRoles = sites.filter((role) => {
        if (!role.endDate) return true;
        return new Date(role.endDate) > new Date();
      });

      if (activeRoles.length === 0) {
        localStorage.removeItem('user');
        return rejectWithValue(
          `You don't have any active roles assigned. Please contact your administrator.`
        );
      }

      localStorage.setItem('availableRoles', JSON.stringify(activeRoles));

      localStorage.setItem(
        'selectedSubscrSite',
        JSON.stringify(activeRoles[0])
      );

      return { user, sites };
    } catch (err) {
      return rejectWithValue('Session initialization failed');
    }
  }
);

// fetchUserRoles
export const fetchUserRoles = createAsyncThunk(
  'auth/fetchUserRoles',
  async ({ userId, siteId, token }, { rejectWithValue }) => {
    try {
      const encryptedId = await fetchEncryptedToken({ uId: userId, sId: siteId }, token, true);
      if (!encryptedId) throw new Error('Failed to encrypt ID');

      const response = await axiosInstance.get(`/rest/roleConfig/rolesOfuser/${encryptedId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const decryptedRoles = response.data.map((role) => ({
        ...role,
        userSubscrSiteRoleId: decryptData(role.userSubscrSiteRoleId),
        subscrSiteId: decryptData(role.subscrSiteId), // if it's just a string ID
        userId: {
          ...role.userId,
          userId: decryptData(role.userId.userId),
        },
        roleId: {
          ...role.roleId,
          roleName: decryptData(role.roleId.roleName),
        },
      }));
      const roleIds = response.data.map((role) => role.roleId);
      sessionStorage.setItem('rolesPresent', JSON.stringify(decryptedRoles));
      return roleIds;
    } catch (error) {
      return rejectWithValue('Failed to fetch user roles');
    }
  }
);

// ✅ logoutUser
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await axiosInstance.post('/auth/logout');
      dispatch(clearPermissions());
      dispatch(clearPlanFeatures());
      dispatch(clearSetupChecklist());
      sessionStorage.clear();
      broadcastLogout();
      return true;
    } catch (err) {
      dispatch(clearPermissions());
      dispatch(clearSetupChecklist());
      if (err.response) {
        return rejectWithValue({
          status: err.response.status,
          message: 'Logout failed',
        });
      }
      return rejectWithValue({
        status: null,
        message: 'Logout failed',
      });
    } finally {
      sessionStorage.clear();
      localStorage.clear();
    }
  }
);

